// src/utils/fileUtils.js

/**
 * สร้าง URL สำหรับไฟล์ที่อัปโหลด
 * @param {string} filePath - path ของไฟล์ในโฟลเดอร์ uploads
 * @returns {string} - URL เต็มสำหรับเข้าถึงไฟล์
 */
export const getFileUrl = (filePath) => {
  if (!filePath) return null;
  
  // ถ้าเป็น URL เต็มแล้ว ให้ return ตามเดิม
  if (filePath.startsWith('http://') || filePath.startsWith('https://')) {
    return filePath;
  }
  
  // ถ้าเป็น path ที่ขึ้นต้นด้วย /uploads ให้ return ตามเดิม
  if (filePath.startsWith('/uploads/')) {
    const uploadHost = process.env.REACT_APP_UPLOAD_URL || "http://localhost:3000";
    // ถ้า uploadHost มี /uploads อยู่แล้ว ให้ใช้ uploadHost ตรงๆ
    if (uploadHost.endsWith('/uploads')) {
      return `${uploadHost}${filePath}`;
    }
    // ถ้าไม่มี ให้เพิ่ม /uploads
    return `${uploadHost}/uploads${filePath}`;
  }
  
  // ถ้าเป็น path ที่ขึ้นต้นด้วย uploads/ ให้เพิ่ม /
  if (filePath.startsWith('uploads/')) {
    const uploadHost = process.env.REACT_APP_UPLOAD_URL || "http://localhost:3000";
    // ถ้า uploadHost มี /uploads อยู่แล้ว ให้ใช้ uploadHost ตรงๆ
    if (uploadHost.endsWith('/uploads')) {
      return `${uploadHost}/${filePath}`;
    }
    // ถ้าไม่มี ให้เพิ่ม /uploads
    return `${uploadHost}/uploads/${filePath}`;
  }
  
  // ถ้าเป็น path ที่ขึ้นต้นด้วย /topicimages/ ให้เปลี่ยนเป็น /uploads/categories/
  if (filePath.startsWith('/topicimages/')) {
    const uploadHost = process.env.REACT_APP_UPLOAD_URL || "http://localhost:3000";
    const fileName = filePath.replace('/topicimages/', '');
    // ถ้า uploadHost มี /uploads อยู่แล้ว ให้ใช้ uploadHost ตรงๆ
    if (uploadHost.endsWith('/uploads')) {
      return `${uploadHost}/categories/${fileName}`;
    }
    // ถ้าไม่มี ให้เพิ่ม /uploads
    return `${uploadHost}/uploads/categories/${fileName}`;
  }
  
  // ถ้าเป็น path ที่ขึ้นต้นด้วย topicimages/ ให้เปลี่ยนเป็น /uploads/categories/
  if (filePath.startsWith('topicimages/')) {
    const uploadHost = process.env.REACT_APP_UPLOAD_URL || "http://localhost:3000";
    const fileName = filePath.replace('topicimages/', '');
    // ถ้า uploadHost มี /uploads อยู่แล้ว ให้ใช้ uploadHost ตรงๆ
    if (uploadHost.endsWith('/uploads')) {
      return `${uploadHost}/categories/${fileName}`;
    }
    // ถ้าไม่มี ให้เพิ่ม /uploads
    return `${uploadHost}/uploads/categories/${fileName}`;
  }
  
  // ถ้าเป็น path อื่นๆ ให้เพิ่ม /uploads/ ด้านหน้า
  const uploadHost = process.env.REACT_APP_UPLOAD_URL || "http://localhost:3000";
  // ถ้า uploadHost มี /uploads อยู่แล้ว ให้ใช้ uploadHost ตรงๆ
  if (uploadHost.endsWith('/uploads')) {
    return `${uploadHost}/${filePath}`;
  }
  // ถ้าไม่มี ให้เพิ่ม /uploads
  return `${uploadHost}/uploads/${filePath}`;
};

/**
 * สร้าง URL สำหรับรูปภาพหมวดหมู่
 * @param {string} imagePath - path ของรูปภาพ
 * @returns {string} - URL เต็มสำหรับรูปภาพ
 */
export const getCategoryImageUrl = (imagePath) => {
  if (!imagePath) return '/topicimages/topic01.png'; // fallback image
  
  return getFileUrl(imagePath);
};

/**
 * สร้าง URL สำหรับไฟล์เนื้อหา
 * @param {string} filePath - path ของไฟล์
 * @returns {string} - URL เต็มสำหรับไฟล์
 */
export const getContentFileUrl = (filePath) => {
  return getFileUrl(filePath);
};

/**
 * สร้าง URL สำหรับเอกสาร
 * @param {string} filePath - path ของเอกสาร
 * @returns {string} - URL เต็มสำหรับเอกสาร
 */
export const getDocumentUrl = (filePath) => {
  return getFileUrl(filePath);
};

/**
 * ตรวจสอบว่าเป็นไฟล์รูปภาพหรือไม่
 * @param {string} filename - ชื่อไฟล์
 * @returns {boolean} - true ถ้าเป็นไฟล์รูปภาพ
 */
export const isImageFile = (filename) => {
  if (!filename) return false;
  
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
  const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  
  return imageExtensions.includes(extension);
};

/**
 * ตรวจสอบว่าเป็นไฟล์เอกสารหรือไม่
 * @param {string} filename - ชื่อไฟล์
 * @returns {boolean} - true ถ้าเป็นไฟล์เอกสาร
 */
export const isDocumentFile = (filename) => {
  if (!filename) return false;
  
  const documentExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.txt'];
  const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  
  return documentExtensions.includes(extension);
};

/**
 * ดึงชื่อไฟล์จาก path
 * @param {string} filePath - path ของไฟล์
 * @returns {string} - ชื่อไฟล์
 */
export const getFileName = (filePath) => {
  if (!filePath) return '';
  
  return filePath.split('/').pop();
};

/**
 * ดึงนามสกุลไฟล์
 * @param {string} filename - ชื่อไฟล์
 * @returns {string} - นามสกุลไฟล์
 */
export const getFileExtension = (filename) => {
  if (!filename) return '';
  
  return filename.toLowerCase().substring(filename.lastIndexOf('.'));
};
